/**
 * Strict validation of the paged Vouchers "Export All" (audit VEP-1, 2026-10-06).
 *
 * The client builds a CSV only from pages it has proven are well-formed and contiguous. Every row must have the
 * shape the CSV needs, and the keyset key (created_at, id) must strictly descend within a page AND across pages,
 * starting below the cursor that was sent. A repeated, overlapping or out-of-order row is a refusal, never a
 * silent de-duplication. Any violation throws ExportValidationError and nothing is downloaded.
 *
 * Timestamps are compared at full microsecond precision (Postgres sends e.g. 2026-10-06T22:00:06.356388+00:00;
 * a JS Date would truncate to milliseconds and could misjudge two rows 0.4 ms apart).
 */

export class ExportValidationError extends Error {}

export interface ExportKey {
  micros: bigint;
  id: string;
  raw: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const TS_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}(?::?\d{2})?)$/;

/** ISO-8601 timestamp -> microseconds since epoch (UTC), or null if it is not a valid timestamp. */
export function parseTimestampMicros(value: unknown): bigint | null {
  if (typeof value !== "string") {
    return null;
  }
  const m = TS_RE.exec(value);
  if (!m) {
    return null;
  }
  const [, y, mo, d, h, mi, s, frac = "", tz] = m;
  const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  const check = new Date(ms);
  if (
    Number.isNaN(ms) ||
    check.getUTCFullYear() !== +y ||
    check.getUTCMonth() !== +mo - 1 ||
    check.getUTCDate() !== +d ||
    +h > 23 ||
    +mi > 59 ||
    +s > 59
  ) {
    return null;
  }
  let offsetMin = 0;
  if (tz !== "Z") {
    const sign = tz[0] === "-" ? -1 : 1;
    const digits = tz.slice(1).replace(":", "");
    offsetMin =
      sign * (+digits.slice(0, 2) * 60 + +(digits.slice(2, 4) || "0"));
  }
  return (
    (BigInt(ms) - BigInt(offsetMin) * 60000n) * 1000n +
    BigInt(frac.padEnd(6, "0"))
  );
}

/** -1 if a sorts before b in export order (newer first), 1 if after, 0 if equal. */
function compareDesc(a: ExportKey, b: ExportKey): number {
  if (a.micros !== b.micros) {
    return a.micros > b.micros ? -1 : 1;
  }
  if (a.id !== b.id) {
    return a.id > b.id ? -1 : 1;
  }
  return 0;
}

/** Why a row cannot go into the CSV, or null if it is fine. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function rowProblem(row: any): string | null {
  if (row === null || typeof row !== "object" || Array.isArray(row)) {
    return "not a voucher record";
  }
  if (typeof row.id !== "string" || !UUID_RE.test(row.id.toLowerCase())) {
    return "missing or invalid id";
  }
  if (parseTimestampMicros(row.created_at) === null) {
    return "missing or invalid created date";
  }
  if (typeof row.voucher_code !== "string" || row.voucher_code.trim() === "") {
    return "missing voucher code";
  }
  if (typeof row.email !== "string") {
    return "missing email";
  }
  if (
    typeof row.discount_value !== "number" ||
    !Number.isFinite(row.discount_value)
  ) {
    return "missing or invalid voucher value";
  }
  if (
    row.applied_amount !== null &&
    row.applied_amount !== undefined &&
    (typeof row.applied_amount !== "number" ||
      !Number.isFinite(row.applied_amount))
  ) {
    return "invalid applied amount";
  }
  if (
    row.is_redeemed !== null &&
    row.is_redeemed !== undefined &&
    typeof row.is_redeemed !== "boolean"
  ) {
    return "invalid redeemed flag";
  }
  return null;
}

export function keyOf(row: { id: string; created_at: string }): ExportKey {
  return {
    micros: parseTimestampMicros(row.created_at) as bigint,
    id: row.id.toLowerCase(),
    raw: row.created_at,
  };
}

/**
 * Validate one page. `after` is the key of the last row already accepted (the cursor sent for this page), or null
 * for the first page. Returns the key of this page's last row (or `after` for an empty page).
 */
export function validateExportPage(
  rows: unknown,
  after: ExportKey | null,
  pageNo: number,
  pageSize: number,
): ExportKey | null {
  if (!Array.isArray(rows)) {
    throw new ExportValidationError(
      `The export received an unexpected reply on page ${pageNo}. Nothing was downloaded.`,
    );
  }
  if (rows.length > pageSize) {
    throw new ExportValidationError(
      `The export received ${rows.length} rows on page ${pageNo}, more than the ${pageSize} asked for. Nothing was downloaded.`,
    );
  }
  let prev = after;
  rows.forEach((row, i) => {
    const problem = rowProblem(row);
    if (problem) {
      throw new ExportValidationError(
        `The export received an invalid voucher on page ${pageNo} (row ${i + 1}: ${problem}). Nothing was downloaded.`,
      );
    }
    const key = keyOf(row);
    if (prev && compareDesc(prev, key) >= 0) {
      throw new ExportValidationError(
        `The export received a repeated or out-of-order voucher on page ${pageNo} (row ${i + 1}). Nothing was downloaded.`,
      );
    }
    prev = key;
  });
  return prev;
}
